import type { ReactNode } from 'react';

// Formatação simples do texto do assistente, sem biblioteca nenhuma:
// parágrafos, quebras de linha, **negrito**, listas ("- " ou "1. ") e uma
// tabela markdown (| a | b |) que rola na horizontal no celular.

function renderInline(texto: string, chave: string): ReactNode[] {
  const partes = texto.split(/(\*\*[^*]+\*\*)/g).filter((p) => p !== '');
  return partes.map((parte, i) => {
    if (parte.startsWith('**') && parte.endsWith('**') && parte.length > 4) {
      return (
        <strong key={`${chave}-${i}`} className="font-bold">
          {parte.slice(2, -2)}
        </strong>
      );
    }
    return <span key={`${chave}-${i}`}>{parte}</span>;
  });
}

function ehLinhaTabela(linha: string): boolean {
  const t = linha.trim();
  return t.startsWith('|') && t.endsWith('|') && t.length > 1;
}

function ehSeparadorTabela(linha: string): boolean {
  return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/.test(linha.trim());
}

function celulas(linha: string): string[] {
  return linha
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

function renderBloco(bloco: string, chave: string): ReactNode {
  const linhas = bloco.split('\n').filter((l) => l.trim() !== '');
  if (linhas.length === 0) return null;

  // Tabela markdown.
  if (linhas.length >= 2 && linhas.every(ehLinhaTabela)) {
    const cabecalho = celulas(linhas[0]);
    const corpo = linhas.slice(1).filter((l) => !ehSeparadorTabela(l)).map(celulas);
    return (
      <div key={chave} className="my-1.5 overflow-x-auto rounded-xl border border-background-200">
        <table className="w-full min-w-[320px] border-collapse text-left text-[13px]">
          <thead>
            <tr className="bg-background-100">
              {cabecalho.map((c, i) => (
                <th key={i} className="whitespace-nowrap px-3 py-2 font-bold text-foreground-800">
                  {renderInline(c, `${chave}-th-${i}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {corpo.map((linha, i) => (
              <tr key={i} className="border-t border-background-200">
                {linha.map((c, j) => (
                  <td key={j} className="whitespace-nowrap px-3 py-2 text-foreground-700">
                    {renderInline(c, `${chave}-td-${i}-${j}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Lista ("- item" ou "1. item").
  const ehLista = linhas.every((l) => /^(-|\d+\.)\s+/.test(l.trim()));
  if (ehLista) {
    const ordenada = /^\d+\./.test(linhas[0].trim());
    const Tag = ordenada ? 'ol' : 'ul';
    return (
      <Tag
        key={chave}
        className={`my-1 space-y-1 pl-5 text-[14px] leading-relaxed ${
          ordenada ? 'list-decimal' : 'list-disc'
        }`}
      >
        {linhas.map((l, i) => (
          <li key={i}>{renderInline(l.trim().replace(/^(-|\d+\.)\s+/, ''), `${chave}-li-${i}`)}</li>
        ))}
      </Tag>
    );
  }

  // Parágrafo normal, com quebras de linha simples.
  return (
    <p key={chave} className="text-[14px] leading-relaxed text-foreground-900">
      {linhas.map((l, i) => (
        <span key={i}>
          {renderInline(l, `${chave}-p-${i}`)}
          {i < linhas.length - 1 && <br />}
        </span>
      ))}
    </p>
  );
}

export default function TextoAssistente({ texto }: { texto: string }) {
  const blocos = texto.split(/\n\s*\n/).filter((b) => b.trim() !== '');
  if (blocos.length === 0) return null;
  return <div className="space-y-2">{blocos.map((b, i) => renderBloco(b, `b${i}`))}</div>;
}